// Dialog for creating a new project. Controlled — the parent owns the
// `open` state. This matters because the Switcher renders the trigger
// inside a Base UI Select.Popup, and a self-managing Dialog there ends
// up sharing focus/keyboard state with the Select, which eats keystrokes
// inside the dialog's input. Lifting state out side-steps the conflict.

import { Dialog } from "@base-ui-components/react/dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { Pill } from "@/components/glass";

interface NewProjectDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreated?: (projectId: string) => void;
}

interface CreatedProject {
	project: { id: string; name: string };
}

async function createProject(name: string): Promise<CreatedProject> {
	const r = await fetch("/api/projects", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ name }),
	});
	if (!r.ok) {
		const body = await r.json().catch(() => ({}));
		throw new Error(body.error || `create failed (${r.status})`);
	}
	return (await r.json()) as CreatedProject;
}

export function NewProjectDialog({ open, onOpenChange, onCreated }: NewProjectDialogProps) {
	const [name, setName] = useState("");
	const [error, setError] = useState<string | null>(null);
	const navigate = useNavigate();
	const qc = useQueryClient();

	const mutation = useMutation({
		mutationFn: createProject,
		onSuccess: (data) => {
			qc.invalidateQueries({ queryKey: ["projects"] });
			onOpenChange(false);
			setName("");
			setError(null);
			if (onCreated) onCreated(data.project.id);
			else navigate(`/projects/${data.project.id}/graph`);
		},
		onError: (e: Error) => setError(e.message),
	});

	useEffect(() => {
		if (!open) {
			setName("");
			setError(null);
		}
	}, [open]);

	const submit = (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = name.trim();
		if (!trimmed) return;
		mutation.mutate(trimmed);
	};

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Backdrop className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[starting-style]:opacity-0 data-[ending-style]:opacity-0 transition-opacity duration-150" />
				<Dialog.Popup
					className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[420px] rounded-[var(--radius-panel)] p-5 data-[starting-style]:opacity-0 data-[starting-style]:scale-95 data-[ending-style]:opacity-0 data-[ending-style]:scale-95 transition-all duration-150"
					style={{
						background: "rgb(20 20 23 / 0.98)",
						backdropFilter: "blur(20px) saturate(180%)",
						WebkitBackdropFilter: "blur(20px) saturate(180%)",
						border: "1px solid rgb(255 255 255 / 0.1)",
						boxShadow: "0 24px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)",
					}}
				>
					<div className="flex items-center justify-between mb-4">
						<Dialog.Title className="text-[14px] font-medium">New project</Dialog.Title>
						<Dialog.Close className="text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors">
							<X className="w-4 h-4" />
						</Dialog.Close>
					</div>
					<form onSubmit={submit} className="space-y-3">
						<div>
							<label className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
								Name
							</label>
							<input
								type="text"
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="aurora"
								autoFocus
								autoComplete="off"
								spellCheck={false}
								className="w-full mt-1 h-9 px-3 rounded-[var(--radius-pill)] bg-[var(--color-surface-solid)] border border-[var(--color-border)] text-[13px] outline-none focus:border-[var(--color-border-hi)]"
							/>
						</div>
						{error && (
							<div className="text-[12px] text-[var(--color-danger)]">{error}</div>
						)}
						<div className="flex justify-end gap-2 pt-1">
							<Dialog.Close render={<Pill>Cancel</Pill>} />
							<Pill type="submit" disabled={!name.trim() || mutation.isPending}>
								{mutation.isPending ? (
									<Loader2 className="w-3.5 h-3.5 animate-spin" />
								) : (
									<Plus className="w-3.5 h-3.5" />
								)}
								Create
							</Pill>
						</div>
					</form>
				</Dialog.Popup>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
